// Lazy artifact loader + one-run facade around the pure entity resolver.
// The immutable auto index is built once per page session; only document
// counts, user terms and suppressions are rebuilt for a translation run.
import { restoreRow } from './glossary.js';
import {
  compileAutoArtifact, createEntityContext, protectResolvedRow,
  resolveRow, signatureForResolution,
} from './entity_lexicon.js';

let lexiconPromise = null;
let compiledLexicon = null;

export async function loadAutoLexicon() {
  if (compiledLexicon) {
    return { lexicon: compiledLexicon, metrics: { cold: false, loadMs: 0, indexMs: 0 } };
  }
  lexiconPromise ??= (async () => {
    const load0 = performance.now();
    const [module, policyModule] = await Promise.all([
      import('./hard_names.json'),
      import('./hard_name_runtime_policy.json'),
    ]);
    const loadMs = performance.now() - load0;
    const index0 = performance.now();
    const lexicon = compileAutoArtifact(module.default, policyModule.default);
    const indexMs = performance.now() - index0;
    compiledLexicon = lexicon;
    return { lexicon, metrics: { cold: true, loadMs, indexMs } };
  })()
    .catch((err) => {
      lexiconPromise = null;
      throw err;
    });
  return lexiconPromise;
}

export function createAutoGlossaryRun({
  documentText,
  userCompiled,
  autoLexicon,
  suppressions,
} = {}) {
  const ctx = createEntityContext({
    documentText, userCompiled, autoLexicon, suppressions,
  });
  const cache = new Map();
  let resolveMs = 0;
  let resolveCalls = 0;
  let cacheHits = 0;
  const autoMarks = new WeakMap();
  const failedAutoProtection = [];
  const resolutionFor = (row) => {
    let resolution = cache.get(row);
    if (resolution) {
      cacheHits++;
    } else {
      const started = performance.now();
      resolution = resolveRow(row, ctx);
      resolveMs += performance.now() - started;
      resolveCalls++;
      // A novel commonly has tens of thousands of unique rows but only a
      // handful with glossary candidates. Retain candidates for the TM →
      // protect hand-off; rematching a no-candidate row is cheaper than a
      // document-sized Map of empty resolution objects on mobile.
      if (resolution.spans.length || resolution.candidateSpans.length) {
        cache.set(row, resolution);
      }
    }
    return resolution;
  };
  const glossary = {
    protect: (row) => {
      const resolution = resolutionFor(row);
      const protectedRow = protectResolvedRow(row, resolution);
      if (protectedRow) {
        autoMarks.set(protectedRow.marks, resolution.protectedSpans
          .some((span) => span.term.source === 'auto'));
      }
      return protectedRow;
    },
    restore: (out, marks) => {
      const restored = restoreRow(out, marks);
      if (!restored.ok) failedAutoProtection.push(autoMarks.get(marks) === true);
      return restored;
    },
  };
  const takeUserRetryRows = (failedRows) => {
    const failed = [...(failedRows ?? [])];
    const kinds = failedAutoProtection.splice(0, failed.length);
    // A mismatch should not happen: engine restore calls and glossaryFailed
    // are recorded in the same loop. Fail safe toward preserving user terms.
    if (kinds.length !== failed.length) return failed;
    return failed.filter((_, index) => kinds[index]);
  };
  const signature = (row) => signatureForResolution(resolutionFor(row), {
    artifactRevision: ctx.artifactRevision,
    protocol: ctx.protocol,
  });
  const summary = () => {
    const selectedByZh = new Map();
    const rejectedSpans = { frequency: 0, suppressed: 0, context: 0, overlap: 0 };
    let autoDictRows = 0;
    let markerOverflowTerms = 0;
    for (const resolution of cache.values()) {
      const selected = resolution.spans.filter((span) => span.term.source === 'auto');
      if (selected.length) autoDictRows++;
      const rowOverflow = new Set(
        resolution.overflowSpans
          .filter((span) => span.term.source === 'auto')
          .map((span) => span.term.zh),
      );
      markerOverflowTerms += rowOverflow.size;
      for (const rejected of resolution.rejectedSpans) {
        if (Object.hasOwn(rejectedSpans, rejected.reason)) {
          rejectedSpans[rejected.reason]++;
        }
      }
      for (const span of selected) {
        const item = selectedByZh.get(span.term.zh) ?? { selected: 0, protected: 0, overflow: 0 };
        item.selected++;
        if (resolution.protectedSpans.some((p) => p.start === span.start
          && p.end === span.end && p.term.source === 'auto')) item.protected++;
        if (resolution.overflowSpans.some((p) => p.start === span.start
          && p.end === span.end && p.term.source === 'auto')) item.overflow++;
        selectedByZh.set(span.term.zh, item);
      }
    }
    const documentTerms = ctx.documentTerms.map((term) => ({
      ...term,
      ...(selectedByZh.get(term.zh) ?? { selected: 0, protected: 0, overflow: 0 }),
    }));
    return {
      enabled: true,
      artifactRevision: ctx.artifactRevision,
      protocol: ctx.protocol,
      autoDictRows,
      markerOverflowTerms,
      rejectedSpans,
      resolvedUniqueRows: cache.size,
      resolveCalls,
      cacheHits,
      resolveMs,
      documentTerms,
    };
  };
  return { ctx, glossary, signature, resolutionFor, takeUserRetryRows, summary };
}
